import { Dialog, Portal } from '@chakra-ui/react';
import { ITenant, UserRoleEnum } from '@monetize/types/app';
import { DEFAULT_PAGER, TDataTablePager } from '@monetize/types/common';
import { useQueryClient } from '@tanstack/react-query';
import { FC, useEffect, useId } from 'react';
import { MdSettings } from 'react-icons/md';
import {
  Link as ReactDomLink,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { useAtom } from 'jotai';
import { useAcceptInvite, useGetTenants } from '../../api/tenantService';
import { ROUTES } from '../../constants';
import { TenantCard } from '../../routes/Tenants/components/TenantCard';
import { TenantTabs } from '../../routes/Tenants/components/TenantTabs';
import { useAuth } from '../../services/auth0';
import { tempRedirectInfoState } from '../../store/global.store';
import {
  MButton,
  MCloseButton,
  MFlex,
  MIcon,
  MLink,
  MPageLoader,
  MText,
  MVStack,
} from '../Monetize';
import { keepOpenForToasts } from '../Monetize/Overlays';

const pager: TDataTablePager = {
  ...DEFAULT_PAGER,
  rows: 100000,
  sortField: 'name',
  sortOrder: 1,
};

interface AppTenantSwitchModalProps {
  onClose: () => void;
  onOpen: () => void;
  isOpen: boolean;
  persistentModal?: boolean;
  showLogout?: boolean;
  shouldSelectFirstOnlyTenant?: boolean;
}

const AppTenantSwitchModal: FC<AppTenantSwitchModalProps> = ({
  onClose,
  onOpen,
  isOpen,
  persistentModal,
  showLogout,
  shouldSelectFirstOnlyTenant,
}: AppTenantSwitchModalProps) => {
  const {
    tenantId,
    currentTenantUserHasSpecificRole,
    currentTenant,
    error: currentTenantError,
  } = useAuth();
  const [redirectInfo, setRedirectInfo] = useAtom(tempRedirectInfoState);
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { mutateAsync: doAcceptInvite, isPending: isAcceptInviteLoading } =
    useAcceptInvite();
  const {
    data: tenants = [],
    isLoading,
    error: fetchTenantsError,
  } = useGetTenants({ config: pager });
  // Labels the dialog by its header, as v2's ModalHeader did — see
  // `MCenterModal.tsx`.
  const titleId = useId();

  useEffect(() => {
    // If there is only 1 tenant, simply navigate to it.
    if (tenants && tenants.length === 1 && shouldSelectFirstOnlyTenant) {
      onSwitchTenant(tenants[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldSelectFirstOnlyTenant, tenants]);

  const onSwitchTenant = (tenant: ITenant) => {
    if (tenant && tenant.id === tenantId) {
      onClose();
      // Check if there's a redirect URL to navigate to
      if (redirectInfo?.redirectUrl) {
        const { redirectUrl } = redirectInfo;
        setRedirectInfo(null);
        navigate(redirectUrl);
      } else if (location.pathname === ROUTES.ONBOARD_SELECT_TENANT) {
        navigate(ROUTES.DASHBOARD);
      }
    } else {
      onClose();

      // consume redirectInfo and set null
      setRedirectInfo(null);
      navigate(
        ROUTES.getSwitchTenantRoute(tenant.id, redirectInfo?.redirectUrl || ''),
      );
      // If tenant is modified, clear all query cache for all queries
      queryClient.clear();
    }
  };

  const onManageTenants = () => {
    navigate(ROUTES.TENANT_LIST);
    onClose();
  };

  return (
    <Dialog.Root
      ids={{ title: titleId }}
      preventScroll
      closeOnInteractOutside={!persistentModal}
      onInteractOutside={keepOpenForToasts}
      placement="center"
      motionPreset="slide-in-bottom"
      size="md"
      open={isOpen}
      onOpenChange={(e) => {
        if (!e.open) {
          onClose();
        }
      }}
    >
      <Portal>
        <Dialog.Backdrop />
        <Dialog.Positioner>
          <Dialog.Content>
            <Dialog.Header id={titleId} fontSize="lg" fontWeight="600" py={3}>
              <MFlex
                w="100%"
                justifyContent="space-between"
                alignItems="center"
              >
                Switch Tenant
                <MFlex alignItems="center" gap={2}>
                  {!fetchTenantsError &&
                    !currentTenantError &&
                    !showLogout &&
                    currentTenantUserHasSpecificRole(
                      UserRoleEnum.TENANT_ADMIN,
                    ) && (
                      <MButton
                        variant="bare"
                        onClick={onManageTenants}
                        data-testid="tenant-manage-btn"
                        size="sm"
                        fontWeight="normal"
                      >
                        <MIcon as={MdSettings} boxSize={4} />
                        Manage
                      </MButton>
                    )}
                  {(showLogout || currentTenantError) && (
                    <MLink fontSize="sm" asChild textDecor="underline">
                      <ReactDomLink to={ROUTES.AUTH_LOGOUT}>
                        Log out
                      </ReactDomLink>
                    </MLink>
                  )}

                  {!persistentModal && !currentTenantError && (
                    <Dialog.CloseTrigger
                      asChild
                      position="relative"
                      top="0"
                      right="0"
                    >
                      <MCloseButton size="md" />
                    </Dialog.CloseTrigger>
                  )}
                </MFlex>
              </MFlex>
            </Dialog.Header>

            <Dialog.Body
              maxHeight="500px"
              overflow="auto"
              className="custom-scroll-bar-v1"
              pt={0}
              pb={4}
            >
              {!isLoading && (currentTenantError || fetchTenantsError) && (
                <>
                  <MText
                    fontSize="md"
                    textAlign="center"
                    fontWeight="600"
                    mb="2"
                  >
                    Error
                  </MText>
                  <MText textAlign="center">
                    {currentTenantError
                      ? 'You don’t have access to any tenants. Make sure you are logged in using the correct user account or reach out to your administrator for assistance.'
                      : ''}
                    {fetchTenantsError
                      ? 'There was a problem finding your tenants.'
                      : ''}
                  </MText>
                </>
              )}
              {isLoading || isAcceptInviteLoading ? (
                <MPageLoader height="150px" />
              ) : (
                <>
                  {!currentTenantError && currentTenant && (
                    <MVStack align="stretch" pt={2} mb={4}>
                      <TenantCard // current tenant card
                        tenant={currentTenant}
                        isCurrentTenant
                        onClick={() => onSwitchTenant(currentTenant)}
                        onAccept={() =>
                          doAcceptInvite({ id: currentTenant.id })
                        }
                      />
                    </MVStack>
                  )}

                  {!fetchTenantsError && (
                    <TenantTabs // test and live tenants
                      tenants={tenants}
                      onSwitchTenant={onSwitchTenant}
                      onAcceptInvite={({ id }) => doAcceptInvite({ id })}
                    />
                  )}
                </>
              )}
            </Dialog.Body>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
};

export default AppTenantSwitchModal;
